import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Award,
  Globe,
  Clock,
  ArrowRight,
  FileText,
  Scale,
  Headphones,
  Briefcase,
  ShieldCheck,
  CheckCircle,
  MessageCircle,
  Zap,
  Upload,
  Search,
  Calculator,
  CreditCard,
  UserCheck,
  PenTool,
  BadgeCheck,
  Package,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import assetUrl from '../assetUrl';
import FAQSection from '../components/home/FAQSection';
import './AboutPage.css';

function useScrollReveal(selector) {
  const containerRef = useRef(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    const items = root.querySelectorAll(selector);
    if (items.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    );

    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [selector]);

  return containerRef;
}

export default function AboutPage() {
  const { t } = useLanguage();
  const a = t.about || {};
  const pageRef = useScrollReveal('.reveal');

  const services = [
    { key: 'professionalTranslation', icon: FileText, link: '/services/type/professional' },
    { key: 'officialTranslation', icon: Scale, link: '/services/type/official' },
    { key: 'interpreting', icon: Headphones, link: '/services/type/interpreting' },
    { key: 'additionalServices', icon: Briefcase, link: '/services' },
  ];

  const team = [
    { icon: FileText, label: a.translators },
    { icon: Headphones, label: a.interpreters },
    { icon: Scale, label: a.lawyers },
    { icon: Award, label: a.notaries },
  ];

  const whyIcons = [Award, Globe, Briefcase, ShieldCheck, MessageCircle, Clock, Zap];
  const commitmentIcons = [Award, ShieldCheck, CheckCircle, Clock, Users];
  const processIcons = [Upload, Search, Calculator, CreditCard, UserCheck, PenTool, BadgeCheck, Package];

  return (
    <div ref={pageRef} className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <div
          className="about-hero-bg"
          style={{ backgroundImage: `url(${assetUrl('/images/hero-sworn-translation.jpg')})` }}
          aria-hidden="true"
        />
        <div className="about-hero-veil" aria-hidden="true" />
        <div className="container about-hero-inner reveal">
          <h1>{a.title}</h1>
          <p className="about-hero-subtitle">{a.subtitle}</p>
          <p className="about-hero-tagline">{a.heroTagline}</p>
        </div>
      </section>

      {/* Who we are */}
      <section className="about-section about-who">
        <div className="container about-who-wrap">
          <div className="about-who-text reveal">
            <h2>{a.whoWeAreTitle}</h2>
            <p className="about-lead">{a.whoWeAreIntro}</p>
            <div className="about-mvv">
              <div>
                <Globe size={18} />
                <strong>{a.mission}</strong>
              </div>
              <div>
                <Award size={18} />
                <strong>{a.vision}</strong>
              </div>
            </div>
            <div className="about-who-footer">
              <span className="about-positioning">{a.positioning}</span>
              <span className="about-values-text">{a.values}</span>
            </div>
          </div>
          <div className="about-who-visual reveal">
            <div className="about-who-frame" aria-hidden="true" />
            <div className="about-who-image">
              <img src={assetUrl('/images/home-zigzag-2.jpg')} alt={a.whoWeAreTitle} />
            </div>
            <div className="about-who-badge">
              <Globe size={28} />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="about-section about-services">
        <div
          className="about-services-bg"
          style={{ backgroundImage: `url(${assetUrl('/images/home-zigzag-2.jpg')})` }}
          aria-hidden="true"
        />
        <div className="about-services-veil" aria-hidden="true" />
        <div className="container">
          <div className="about-section-header reveal">
            <span className="about-section-kicker">{a.servicesTitle}</span>
            <h2>{a.servicesTitle}</h2>
            <p>{a.servicesIntro}</p>
          </div>
          <div className="about-services-grid">
            {services.map(({ key, icon: Icon, link }, index) => (
              <Link key={key} to={link} className="about-service-card reveal" style={{ transitionDelay: `${index * 80}ms` }}>
                <div className="about-service-icon">
                  <Icon size={32} />
                </div>
                <h3>{a[key]}</h3>
                <span className="about-service-arrow">
                  <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="about-section about-team">
        <div className="container">
          <div className="about-team-grid reveal">
            <div className="about-team-media">
              <img src={assetUrl('/images/earth-map.jpg')} alt={a.teamTitle} />
            </div>
            <div className="about-team-content">
              <h2>{a.teamTitle}</h2>
              <p>{a.teamIntro}</p>
              <div className="about-team-roles">
                {team.map(({ icon: Icon, label }, index) => (
                  <div key={label} className="about-team-role reveal" style={{ transitionDelay: `${index * 80}ms` }}>
                    <div className="about-team-icon">
                      <Icon size={24} />
                    </div>
                    <h3>{label}</h3>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="processus" className="about-section about-process">
        <div className="container">
          <div className="about-section-header reveal">
            <span className="about-section-kicker">{a.processTitle}</span>
            <h2>{a.processTitle}</h2>
            <p>{a.processIntro}</p>
          </div>
          <div className="about-process-grid reveal">
            <div className="about-process-timeline">
              {a.processSteps?.map(({ title, desc }, index) => {
                const ProcessIcon = processIcons[index % processIcons.length];
                return (
                  <div key={title} className="about-process-step" style={{ transitionDelay: `${index * 80}ms` }}>
                    <span className="about-process-dot">
                      <ProcessIcon size={22} />
                    </span>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="about-section about-why">
        <div className="container">
          <div className="about-why-grid reveal">
            <div className="about-why-content">
              <span className="about-section-kicker">{a.whyTitle}</span>
              <h2>{a.whyTitle}</h2>
              <p>{a.whyIntro}</p>
              <div className="about-why-list">
                {a.whyPoints?.map(({ title, desc }, index) => {
                  const Icon = whyIcons[index % whyIcons.length];
                  return (
                    <div key={title} className="about-why-item reveal" style={{ transitionDelay: `${index * 80}ms` }}>
                      <div className="about-why-icon">
                        <Icon size={20} />
                      </div>
                      <div>
                        <h3>{title}</h3>
                        <p>{desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="about-section about-commitments">
        <div className="container">
          <div className="about-section-header reveal">
            <span className="about-section-kicker">{a.commitmentTitle}</span>
            <h2>{a.commitmentTitle}</h2>
            <p>{a.commitmentIntro}</p>
          </div>
          <div className="about-commitments-grid">
            {a.commitments?.map((label, index) => {
              const Icon = commitmentIcons[index % commitmentIcons.length];
              return (
                <div key={label} className="about-commitment reveal" style={{ transitionDelay: `${index * 80}ms` }}>
                  <Icon size={20} />
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* CTA */}
      <section className="about-section about-cta">
        <div
          className="about-cta-bg"
          style={{ backgroundImage: `url(${assetUrl('/images/earth-map.jpg')})` }}
          aria-hidden="true"
        />
        <div className="about-cta-veil" aria-hidden="true" />
        <div className="container about-cta-inner reveal">
          <h2>{a.ctaTitle}</h2>
          <p>{a.ctaText}</p>
          <div className="about-cta-actions">
            <Link to="/order" className="btn btn-primary">
              {a.orderButton}
              <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="btn btn-outline">
              {a.contactButton}
              <MessageCircle size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
